import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './AdminDashboard.css';

const TestComparisonPage = () => {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [parentGeneration, setParentGeneration] = useState(null);
  const [testGenerations, setTestGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState('compact'); // 'compact' or 'large'
  const [promptTemplateNames, setPromptTemplateNames] = useState({}); // Map of template ID to name

  const formatGenerationTime = (seconds) => {
    if (!seconds || seconds < 0) return 'N/A';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    
    if (minutes === 0) {
      return `${remainingSeconds}s`;
    } else if (remainingSeconds === 0) {
      return `${minutes}m`;
    } else {
      return `${minutes}m ${remainingSeconds}s`;
    }
  };

  const handleImageClick = (imageUrl) => {
    if (viewMode === 'large') {
      window.open(imageUrl, '_blank');
    }
  };

  useEffect(() => {
    const checkAdmin = async () => {
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdTokenResult();
        setIsAdmin(idToken.claims.admin === true);
      }
    };
    checkAdmin();
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    const fetchGenerations = async () => {
      try {
        setLoading(true);
        const functions = getFunctions();
        
        // Fetch parent generation details
        const getGenerationDetails = httpsCallable(functions, 'getGenerationDetailsForAdmin');
        const parentResult = await getGenerationDetails({ generationId: parentId });
        const parentGen = parentResult.data;
        setParentGeneration(parentGen);

        // Fetch test generations for this parent
        const getTestGenerations = httpsCallable(functions, 'getTestGenerationsByParentId');
        const testResult = await getTestGenerations({ parentId });
        
        setTestGenerations(testResult.data.testGenerations);
        
        // Fetch prompt template names
        const templateIds = new Set();
        if (parentGen.promptTemplateId) {
          templateIds.add(parentGen.promptTemplateId);
        }
        testResult.data.testGenerations.forEach(test => {
          if (test.promptTemplateId) {
            templateIds.add(test.promptTemplateId);
          }
        });
        
        const templateNamesMap = {};
        for (const templateId of templateIds) {
          try {
            const templateDoc = await getDoc(doc(db, 'promptTemplates', templateId));
            if (templateDoc.exists()) {
              templateNamesMap[templateId] = templateDoc.data().name || templateId;
            }
          } catch (err) {
            console.error(`Error fetching template ${templateId}:`, err);
          }
        }
        setPromptTemplateNames(templateNamesMap);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching generations:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchGenerations();
  }, [parentId, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="admin-dashboard">
        <div className="error-message">Access denied. Admin privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">Loading comparison data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  if (!parentGeneration) {
    return (
      <div className="admin-dashboard">
        <div className="error-message">Parent generation not found</div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard test-comparison-page">
      <div className="admin-header">
        <h1>Test Comparison</h1>
        <div className="header-controls">
          <button 
            onClick={() => setViewMode(viewMode === 'compact' ? 'large' : 'compact')} 
            className="view-toggle-button"
          >
            {viewMode === 'compact' ? '🖼️ Large View' : '⚡ Compact View'}
          </button>
          <button onClick={() => navigate('/admin')} className="back-button">
            Back to Dashboard
          </button>
        </div>
      </div>

      <div className="comparison-header">
        <h2>Parent Generation</h2>
        <div className="generation-info">
          <p><strong>ID:</strong> {parentGeneration.id}</p>
          <p><strong>Prompt:</strong> {promptTemplateNames[parentGeneration.promptTemplateId] || parentGeneration.promptTemplateName || parentGeneration.prompt || parentGeneration.promptTemplateId}</p>
          {parentGeneration.generationModel && (
            <p><strong>Model:</strong> {parentGeneration.generationModel}</p>
          )}
          <p><strong>Status:</strong> {parentGeneration.status}</p>
          <p><strong>Created:</strong> {new Date(parentGeneration.createdAt).toLocaleString()}</p>
          {parentGeneration.stravaStats?.activityName && (
            <p><strong>Activity:</strong> {parentGeneration.stravaStats.activityName}</p>
          )}
        </div>
      </div>

      <div className={`comparison-grid ${viewMode === 'large' ? 'large-view' : ''}`}>
        <div className="generation-card">
          <h3>Mapbox Original</h3>
          {parentGeneration.originalImageUrl ? (
            <img 
              src={parentGeneration.originalImageUrl} 
              alt="Original Mapbox render"
              className="comparison-image"
              onClick={() => handleImageClick(parentGeneration.originalImageUrl)}
            />
          ) : (
            <div className="no-image">No Mapbox image available</div>
          )}
          <div className="generation-details">
            <p><strong>Type:</strong> Mapbox Render</p>
            <p><strong>Source:</strong> {parentGeneration.gpxDataSourceUsed || 'Unknown'}</p>
          </div>
        </div>

        <div className="generation-card">
          <h3>{promptTemplateNames[parentGeneration.promptTemplateId] || parentGeneration.promptTemplateName || 'AI Generated'}</h3>
          {parentGeneration.generatedImageUrl ? (
            <img 
              src={parentGeneration.generatedImageUrl} 
              alt="AI generated"
              className="comparison-image"
              onClick={() => handleImageClick(parentGeneration.generatedImageUrl)}
            />
          ) : (
            <div className="no-image">No image available</div>
          )}
          <div className="generation-details">
            <p><strong>Prompt Template:</strong> {promptTemplateNames[parentGeneration.promptTemplateId] || parentGeneration.promptTemplateName || parentGeneration.promptTemplateId}</p>
            {parentGeneration.generationModel && (
              <p><strong>Model:</strong> {parentGeneration.generationModel}</p>
            )}
            {parentGeneration.generationDurationSeconds && (
              <p><strong>Generation Time:</strong> {formatGenerationTime(parentGeneration.generationDurationSeconds)}</p>
            )}
            {parentGeneration.customPromptText && (
              <p><strong>Custom Text:</strong> {parentGeneration.customPromptText}</p>
            )}
          </div>
        </div>

        {testGenerations.map((testGen) => (
          <div key={testGen.id} className="generation-card">
            <h3>{testGen.testPromptName || 'Test Variation'}</h3>
            {testGen.generatedImageUrl ? (
              <img 
                src={testGen.generatedImageUrl} 
                alt={`Test generation ${testGen.testPromptName}`}
                className="comparison-image"
                onClick={() => handleImageClick(testGen.generatedImageUrl)}
              />
            ) : (
              <div className="no-image">
                {testGen.status === 'openai_processing_complete' ? 'No image' : `Status: ${testGen.status}`}
              </div>
            )}
            <div className="generation-details">
              <p><strong>Status:</strong> {testGen.status}</p>
              <p><strong>Test ID:</strong> {testGen.id.slice(0, 8)}...</p>
              {testGen.generationModel && (
                <p><strong>Model:</strong> {testGen.generationModel}</p>
              )}
              {testGen.generationDurationSeconds && (
                <p><strong>Generation Time:</strong> {formatGenerationTime(testGen.generationDurationSeconds)}</p>
              )}
              {testGen.error && (
                <p className="error-text"><strong>Error:</strong> {testGen.error}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {testGenerations.length === 0 && (
        <div className="no-test-generations">
          <p>No test generations found for this parent generation.</p>
          <p>Test generations are created automatically when a prompt template has "testInParallel" enabled 
             and other templates exist that start with the same name.</p>
        </div>
      )}

      <style>{`
        .test-comparison-page {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .header-controls {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .back-button,
        .view-toggle-button {
          padding: 8px 16px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .view-toggle-button {
          background: #17a2b8;
        }

        .back-button:hover {
          background: #5a6268;
        }

        .view-toggle-button:hover {
          background: #138496;
        }

        .comparison-header {
          margin-bottom: 30px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .generation-info p {
          margin: 5px 0;
        }

        .comparison-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-top: 30px;
        }

        .comparison-grid.large-view {
          grid-template-columns: 1fr;
          max-width: 1200px;
          margin: 30px auto 0;
        }

        .generation-card {
          border: 1px solid #dee2e6;
          border-radius: 8px;
          padding: 15px;
          background: white;
        }

        .generation-card h3 {
          margin-top: 0;
          margin-bottom: 15px;
          color: #495057;
        }

        .comparison-image {
          width: 100%;
          height: auto;
          border-radius: 4px;
          margin-bottom: 15px;
          display: block;
        }

        .large-view .comparison-image {
          max-width: 100%;
          height: auto;
          cursor: zoom-in;
        }

        .large-view .generation-card {
          padding: 25px;
        }

        .large-view .generation-card h3 {
          font-size: 1.5rem;
          margin-bottom: 20px;
        }

        .no-image {
          width: 100%;
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #e9ecef;
          border-radius: 4px;
          margin-bottom: 15px;
          color: #6c757d;
        }

        .large-view .no-image {
          height: 400px;
          font-size: 1.2rem;
        }

        .generation-details {
          font-size: 14px;
        }

        .generation-details p {
          margin: 5px 0;
          word-break: break-word;
        }

        .error-text {
          color: #dc3545;
        }

        .no-test-generations {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: #6c757d;
        }

        .error-message {
          text-align: center;
          padding: 40px;
          color: #dc3545;
        }
      `}</style>
    </div>
  );
};

export default TestComparisonPage;